import { useQueryClient } from '@tanstack/react-query';
import type { FC, ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  CreateTicketMessageRequestBodyDTO,
  CreateTicketScreenshotMessageRequestBodyDTO,
} from 'declarations/dto/tickets';
import { MessageTypesE } from 'declarations/enums/messages';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { MessageI } from 'declarations/interfaces/messages';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import {
  useFetchTicketMessages,
  useUpdateTicketMessage,
  useUpdateTicketScreenshotMessage,
} from 'hooks/api/useTickets';

import type { NewTicketMessageFormValuesT } from '../components/MessageForm/declarations';

type UnsavedMessageT = Omit<MessageI, 'action' | 'attachmentName' | 'sender'> & {
  file?: Blob | null;
};

type PendingMessageT = {
  assignToOppositeUser: boolean;
  expiresAt: number;
  file: Blob | null;
  sentDate: Date;
  text: string;
  ticketId: string;
  timeoutId: number;
};

type MessagesContextT = {
  messages: MessageI[];
  isLoadingMessages: boolean;
  unsavedMessage: null | UnsavedMessageT;
  isCreatingMessage: boolean;
  isMessageCreationError: boolean;
  createMessageErrorMessage: string | undefined;
  createMessageValidationErrorMessage: string | undefined;
  undoMessageTimeLeft: number;
  handleUndoMessage: NoopT;
  handleConfirmMessage: (assignToWarrCloud: boolean) => void;
  handleFlushPendingMessage: NoopT;
  createScreenshotMessageErrorMessage: string | undefined;
  createScreenShotMessageValidationErrorMessage: string | undefined;
  handleCreateUnsavedMessage: (
    data: NewTicketMessageFormValuesT,
    assignToWarrCloud: boolean
  ) => void;
};

type MessagesProviderPropsT = {
  ticketId: string;
  children: ReactNode;
};

const contextDefaultValue = {
  messages: [],
  isLoadingMessages: false,
  unsavedMessage: null,
  isCreatingMessage: false,
  isMessageCreationError: false,
  createMessageErrorMessage: undefined,
  createMessageValidationErrorMessage: undefined,
  undoMessageTimeLeft: 0,
  handleUndoMessage: (): undefined => undefined,
  handleConfirmMessage: (): undefined => undefined,
  handleFlushPendingMessage: (): undefined => undefined,
  handleCreateUnsavedMessage: (): undefined => undefined,
  createScreenshotMessageErrorMessage: undefined,
  createScreenShotMessageValidationErrorMessage: undefined,
};

export const MessagesContext = createContext<MessagesContextT>(contextDefaultValue);

const saveMessageDelay = 30000;

const MessagesProvider: FC<MessagesProviderPropsT> = ({ ticketId, children }) => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const [unsavedMessage, setUnsavedMessage] = useState<null | UnsavedMessageT>(null);
  const [undoMessageTimeLeft, setUndoMessageTimeLeft] = useState(0);
  const activeTicketIdRef = useRef(ticketId);
  const pendingMessagesRef = useRef<Map<string, PendingMessageT>>(
    new Map<string, PendingMessageT>()
  );
  const previousTicketIdRef = useRef(ticketId);
  const sendPendingMessageRef = useRef<(pendingMessage: PendingMessageT) => void>(() => {});

  /**
   * Api hooks
   */
  const messagesResult = useFetchTicketMessages({
    ticketId,
  });

  const invalidateAfterMessageSend = useCallback(
    (assignToOppositeUser: boolean): void => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (assignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      }
    },
    [queryClient]
  );

  const syncMessagePreview = useCallback((currentTicketId: string): void => {
    const pendingMessage = pendingMessagesRef.current.get(currentTicketId);
    if (!pendingMessage) {
      setUnsavedMessage(null);
      setUndoMessageTimeLeft(0);
      return;
    }

    setUnsavedMessage({
      file: pendingMessage.file,
      sentDate: pendingMessage.sentDate,
      text: pendingMessage.text,
      typeId: MessageTypesE.Text,
    });
    setUndoMessageTimeLeft(Math.max(0, Math.ceil((pendingMessage.expiresAt - Date.now()) / 1000)));
  }, []);

  type TextMessageMutationVariablesT = { ticketId: string } & CreateTicketMessageRequestBodyDTO;
  type ScreenshotMessageMutationVariablesT = {
    ticketId: string;
  } & CreateTicketScreenshotMessageRequestBodyDTO;

  const createTicketMessageMutation = useUpdateTicketMessage({
    onSuccess: (_data, variables, _context): void => {
      const v = variables as TextMessageMutationVariablesT;
      invalidateAfterMessageSend(v.assignToOppositeUser);
      syncMessagePreview(v.ticketId);
    },
    onError: (_error, variables): void => {
      const v = variables as TextMessageMutationVariablesT;
      syncMessagePreview(v.ticketId);
    },
  });

  const createTicketScreenshotMessageMutation = useUpdateTicketScreenshotMessage({
    onSuccess: (_data, variables, _context): void => {
      const v = variables as ScreenshotMessageMutationVariablesT;
      invalidateAfterMessageSend(v.assignToOppositeUser);
      syncMessagePreview(v.ticketId);
    },
    onError: (_error, variables): void => {
      const v = variables as ScreenshotMessageMutationVariablesT;
      syncMessagePreview(v.ticketId);
    },
  });

  /**
   * Variables
   */
  const createMessageErrorMessage = useMemo(
    () => getErrorMessage(createTicketMessageMutation.error),
    [createTicketMessageMutation.error]
  );
  const createMessageValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createTicketMessageMutation.error),
    [createTicketMessageMutation.error]
  );
  const createScreenshotMessageErrorMessage = useMemo(
    () => getErrorMessage(createTicketScreenshotMessageMutation.error),
    [createTicketScreenshotMessageMutation.error]
  );
  const createScreenShotMessageValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createTicketScreenshotMessageMutation.error),
    [createTicketScreenshotMessageMutation.error]
  );

  /**
   * Handlers
   */
  const sendPendingMessage = useCallback(
    (pendingMessage: PendingMessageT): void => {
      const { ticketId: targetTicketId } = pendingMessage;
      pendingMessagesRef.current.delete(targetTicketId);

      if (pendingMessage.file) {
        createTicketScreenshotMessageMutation.mutate({
          assignToOppositeUser: pendingMessage.assignToOppositeUser,
          file: pendingMessage.file,
          text: pendingMessage.text,
          ticketId: targetTicketId,
        });
      } else {
        createTicketMessageMutation.mutate({
          assignToOppositeUser: pendingMessage.assignToOppositeUser,
          text: pendingMessage.text,
          ticketId: targetTicketId,
        });
      }
    },
    [createTicketMessageMutation, createTicketScreenshotMessageMutation]
  );

  useEffect(() => {
    sendPendingMessageRef.current = sendPendingMessage;
  }, [sendPendingMessage]);

  const flushAllPendingMessages = useCallback((): void => {
    Array.from(pendingMessagesRef.current.values()).forEach((pendingMessage): void => {
      window.clearTimeout(pendingMessage.timeoutId);

      sendPendingMessageRef.current(pendingMessage);
    });
  }, []);

  const handleCreateUnsavedMessage = useCallback(
    (data: NewTicketMessageFormValuesT, assignToWarrCloud: boolean): void => {
      const existingPendingMessage = pendingMessagesRef.current.get(ticketId);

      if (existingPendingMessage) {
        window.clearTimeout(existingPendingMessage.timeoutId);

        sendPendingMessage(existingPendingMessage);
      }

      const pendingMessageWithoutTimeout = {
        assignToOppositeUser: assignToWarrCloud,
        expiresAt: Date.now() + saveMessageDelay,
        file: data.file ?? null,
        sentDate: new Date(),
        text: data.message,
        ticketId,
      };

      const timeoutId = window.setTimeout((): void => {
        sendPendingMessage({
          ...pendingMessageWithoutTimeout,
          timeoutId,
        });
      }, saveMessageDelay);

      pendingMessagesRef.current.set(ticketId, {
        ...pendingMessageWithoutTimeout,
        timeoutId,
      });

      syncMessagePreview(ticketId);
    },
    [sendPendingMessage, syncMessagePreview, ticketId]
  );

  const handleConfirmMessage = useCallback(
    (assignToOppositeUser: boolean): void => {
      const pendingMessage = pendingMessagesRef.current.get(ticketId);
      if (!pendingMessage) {
        return;
      }

      window.clearTimeout(pendingMessage.timeoutId);
      sendPendingMessage({
        ...pendingMessage,
        assignToOppositeUser,
      });
    },
    [sendPendingMessage, ticketId]
  );

  const handleUndoMessage = useCallback((): void => {
    const pendingMessage = pendingMessagesRef.current.get(ticketId);
    if (!pendingMessage) {
      return;
    }

    window.clearTimeout(pendingMessage.timeoutId);
    pendingMessagesRef.current.delete(ticketId);
    syncMessagePreview(ticketId);
  }, [syncMessagePreview, ticketId]);

  const handleFlushPendingMessage = useCallback((): void => {
    const pendingMessage = pendingMessagesRef.current.get(ticketId);
    if (!pendingMessage) {
      return;
    }

    window.clearTimeout(pendingMessage.timeoutId);

    sendPendingMessage(pendingMessage);
  }, [sendPendingMessage, ticketId]);

  /**
   * Effects
   */
  useEffect(() => {
    activeTicketIdRef.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    if (previousTicketIdRef.current !== ticketId) {
      previousTicketIdRef.current = ticketId;
    }

    syncMessagePreview(ticketId);
  }, [syncMessagePreview, ticketId]);

  useEffect(() => {
    const intervalId = window.setInterval((): void => {
      const activePendingMessage = pendingMessagesRef.current.get(activeTicketIdRef.current);
      if (!activePendingMessage) {
        setUndoMessageTimeLeft(0);
        return;
      }

      const nextTimeLeft = Math.max(
        0,
        Math.ceil((activePendingMessage.expiresAt - Date.now()) / 1000)
      );
      setUndoMessageTimeLeft(nextTimeLeft);
    }, 500);

    return (): void => {
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (): void => {
      flushAllPendingMessages();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushAllPendingMessages]);

  useEffect(() => {
    return (): void => {
      flushAllPendingMessages();
    };
  }, [flushAllPendingMessages]);

  return (
    <MessagesContext.Provider
      value={{
        messages: messagesResult.data ?? [],
        isLoadingMessages: messagesResult.isLoading,
        unsavedMessage,
        isMessageCreationError:
          createTicketMessageMutation.isError || createTicketScreenshotMessageMutation.isError,
        createMessageErrorMessage,
        createMessageValidationErrorMessage,
        createScreenshotMessageErrorMessage,
        createScreenShotMessageValidationErrorMessage,
        isCreatingMessage:
          createTicketMessageMutation.isLoading || createTicketScreenshotMessageMutation.isLoading,
        undoMessageTimeLeft,
        handleCreateUnsavedMessage,
        handleUndoMessage,
        handleConfirmMessage,
        handleFlushPendingMessage,
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};

const useMessages = (): MessagesContextT => useContext(MessagesContext);

export { MessagesProvider, useMessages };
